/**
 * Recovering from the error page people saw after a deploy.
 *
 * The app is code-split, and every chunk filename carries a hash of its contents. A tab
 * open since before a deploy still holds the old filenames, and those files are gone from
 * the server — so the moment somebody opens a tab they had not opened yet, the import 404s,
 * the error boundary catches it, and they are shown a page saying something went wrong.
 *
 * Nothing went wrong. The app was updated underneath them, and reloading fixes it every
 * time — which makes it exactly the kind of thing the app should do for them rather than
 * show them. A client-facing tool that throws an error page at somebody looks broken, and
 * the explanation ("we deployed while you had it open") never reaches them.
 *
 * Two halves, and the second matters as much as the first: only *this* failure is treated
 * this way. A real bug must still reach the error boundary and be reported, not be quietly
 * papered over by a reload that loses whatever the person was in the middle of.
 */

const STALE_MESSAGES = [
  // Chrome, Safari and Firefox each word it differently, and Vite adds its own.
  'failed to fetch dynamically imported module',
  'error loading dynamically imported module',
  'importing a module script failed',
  'unable to preload css',
  'dynamically imported module',
]

export function isStaleChunkError(error: unknown): boolean {
  if (!(error instanceof Error)) return false
  if (error.name === 'ChunkLoadError') return true
  const message = error.message.toLowerCase()
  return STALE_MESSAGES.some((m) => message.includes(m))
}

/** Where the fact that we already tried is remembered, per tab. */
const KEY = 'mc:stale-chunk-reload'

/**
 * Reloads, but only once per build.
 *
 * Without the guard a chunk that is genuinely missing — a broken deploy rather than a
 * superseded one — would reload forever, which is worse than the error page it replaced.
 * The build stamp is the key, so the next deploy gets its own single attempt.
 */
export function reloadOnceForStaleBuild(
  buildSha: string,
  session: Storage,
  reload: () => void,
): boolean {
  let already: string | null = null
  try { already = session.getItem(KEY) } catch { /* private mode, or storage refused */ }
  if (already === buildSha) return false
  try { session.setItem(KEY, buildSha) } catch { /* proceeding regardless: one reload with
    no memory of it is still better than an error page, and the browser's own reload
    throttling stops a genuine loop. */ }
  reload()
  return true
}
